import { sortByKey } from '@semios/app-platform-common'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { getCropNameFromId } from 'utils/getCropNameFromId'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { getAlternatingRowStyle, icsTableStyles } from '../_utils/icsTableStyles'
import type { TIcsDashboardData } from '../types'
import { DayColumnHeader } from './DayColumnHeader'

export const CropWaterConsumption = ({
  dailyEtNextWeek = [],
}: {
  dailyEtNextWeek: TIcsDashboardData['dailyEtNextWeek']
}) => {
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const selectedProperty = fieldAssetStore.useSelector((s) => s.properties)?.[Number(selectedPropertyId)]
  const etUnitSuffix = unitConverter.evapotranspiration().suffix()
  const rainUnitSuffix = unitConverter.precipitation().suffix()

  const processedZones = useMemo(() => {
    if (!selectedProperty) return []

    const irrigationZoneEmitterValues = Object.values(selectedProperty.irrigationZoneEmitters ?? {})
    const emittersByZone: Record<number, typeof irrigationZoneEmitterValues> = {}

    irrigationZoneEmitterValues.forEach((emitter) => {
      emittersByZone[emitter.irrigationZoneId] ??= []

      emittersByZone[emitter.irrigationZoneId].push(emitter)
    })

    return dailyEtNextWeek
      .map((zone) => {
        const emittersForZone = emittersByZone[zone.zone_id] ?? []
        const foundZoneName = emittersForZone[0]?.name
        const firstBlockId = zone.blockData.block_ids[0]

        const cropId =
          firstBlockId !== undefined ? selectedProperty.blocks?.[firstBlockId]?.cropIds?.[0] : undefined

        const cropName = getCropNameFromId(cropId)

        return { ...zone, zoneName: foundZoneName, emittersForZone, cropName }
      })
      .filter((zone) => !!zone.zoneName)
      .sort(sortByKey('zoneName'))
      .map((zone) => {
        const totalEtInches = (zone?.blockData?.etc ?? []).reduce((acc: number, { value }) => {
          const convertedValue = unitConverter.evapotranspiration(Number(value)).value()

          return acc + (convertedValue || 0)
        }, 0)

        const totalEtMM = (zone?.blockData?.etc ?? []).reduce((acc: number, { value }) => {
          const convertedValue = unitConverter
            .evapotranspiration(Number(value), {
              outputUnit: 'mm',
            })
            .value()

          return acc + (convertedValue || 0)
        }, 0)

        const totalRain = (zone?.blockData?.rain ?? []).reduce((sum: number, { value }) => {
          const convertedValue = unitConverter.precipitation(Number(value)).value()

          return sum + (convertedValue || 0)
        }, 0)

        const emitters = zone.emittersForZone.map((emitter) => {
          const emitterName = mapApiEmitterTypeToLabel(emitter.emitterType)
          const flowRate = emitter.flowRate ?? {}
          const mmPerHour = flowRate.mmPerHour || 0
          const totalHours = mmPerHour > 0 ? (totalEtMM / mmPerHour).toFixed(1) : '—'

          return {
            id: emitter.irrigationZoneEmitterId,
            name: emitterName,
            totalHours,
          }
        })

        return {
          ...zone,
          totalEtInches,
          totalRain,
          emitters,
        }
      })
  }, [dailyEtNextWeek, selectedProperty])

  if (!selectedProperty?.timezone) return null

  const { timezone } = selectedProperty

  return (
    <div>
      <h3>{translate.phrases.banyanApp('Crop Water Consumption (ETc)')}</h3>
      <table css={icsTableStyles.table}>
        <thead>
          <tr>
            <th rowSpan={2} css={icsTableStyles.headerCell}>
              {translate.phrases.banyanApp('Zone Name').toUpperCase()}
            </th>
            <th rowSpan={2} css={icsTableStyles.headerCell}>
              {translate.phrases.banyanApp('Emitter').toUpperCase()}
            </th>

            {/* Day headers */}
            {(processedZones?.[0]?.blockData?.etc ?? []).map(({ timestamp }) => (
              <th key={timestamp} rowSpan={2} css={icsTableStyles.headerCell}>
                <DayColumnHeader day={moment.tz(timestamp, timezone)} />
              </th>
            ))}

            <th colSpan={2} css={icsTableStyles.headerCell}>
              {translate.phrases.banyanApp('Total').toUpperCase()}
            </th>

            <th rowSpan={2} css={icsTableStyles.headerCell}>
              {translate.phrases
                .banyanApp('Rain ({{unit}})', {
                  unit: rainUnitSuffix === 'in' ? 'inches' : 'mm',
                })
                .toUpperCase()}
            </th>
          </tr>

          <tr>
            {/* Sub-headers for total columns */}
            <th css={icsTableStyles.headerCellSmall}>{translate.phrases.banyanApp('Hours').toUpperCase()}</th>
            <th css={icsTableStyles.headerCellSmall}>{etUnitSuffix.toUpperCase()}</th>
          </tr>
        </thead>

        <tbody>
          {processedZones.map((zone, zoneIndex) =>
            zone.emitters.map((emitter, emitterIndex) => (
              <tr key={`${zone.zone_id}-${emitter.id}`} css={getAlternatingRowStyle(zoneIndex)}>
                {/* Zone name */}
                {emitterIndex === 0 ? (
                  <td css={icsTableStyles.bodyCellLeft} rowSpan={zone.emitters.length}>
                    <div>
                      <div css={{ fontWeight: 'bold' }}>{zone.zoneName}</div>
                      <div>
                        <span css={{ fontSize: 12, color: colors.grey800 }}>{zone.cropName}</span>
                      </div>
                    </div>
                  </td>
                ) : null}
                <td css={icsTableStyles.bodyCellLeft}>{emitter.name}</td>

                {/* Daily water consumption values */}
                {emitterIndex === 0
                  ? (zone?.blockData?.etc || []).map(({ timestamp, value }, index) => {
                      const dayKey = moment.tz(timestamp, timezone).format('YYYY-MM-DD')
                      const convertedValue = unitConverter.evapotranspiration(Number(value)).value()
                      const kcValue = zone?.blockData?.kc?.[index]?.value

                      return (
                        <td key={dayKey} css={icsTableStyles.bodyCellData} rowSpan={zone.emitters.length}>
                          <div>
                            <div>{convertedValue ?? '0'}</div>
                            <div>
                              <span css={{ fontSize: 12, color: colors.grey800 }}>
                                {translate.phrases.banyanApp('Kc: {{value}}', {
                                  value:
                                    kcValue !== null && kcValue !== undefined && !isNaN(Number(kcValue))
                                      ? Number(kcValue).toFixed(2)
                                      : '-',
                                })}
                              </span>
                            </div>
                          </div>
                        </td>
                      )
                    })
                  : null}

                {/* Total Hours */}
                <td css={icsTableStyles.bodyCellHighlight}>{emitter.totalHours}</td>

                {/* Total Inches */}
                {emitterIndex === 0 ? (
                  <td css={icsTableStyles.bodyCellHighlight} rowSpan={zone.emitters.length}>
                    {zone.totalEtInches.toFixed(2)}
                  </td>
                ) : null}

                {/* Rain column */}
                {emitterIndex === 0 ? (
                  <td css={icsTableStyles.bodyCellRain} rowSpan={zone.emitters.length}>
                    {zone.totalRain.toFixed(1)}
                  </td>
                ) : null}
              </tr>
            )),
          )}
        </tbody>
      </table>

      <div css={{ marginTop: 10, fontSize: 12, color: colors.grey800 }}>
        <p>
          {translate.phrases.banyanApp('* All values in {{unit}}', {
            unit: etUnitSuffix === 'in' ? 'inches' : 'millimeters',
          })}
        </p>
        <p>{translate.phrases.banyanApp('* Hours = ETc / Application Rate')}</p>
        <p>{translate.phrases.banyanApp('* Application rate is based on the emitter in the zone')}</p>
      </div>
    </div>
  )
}
